import personaBuilderData from '../../../../data/personaBuilder/data.json';
import InputFieldLabel from '../../input-field-label';
import { Button } from '../../button/button';
import type { PersonaDetailsProps } from './types';

export function PersonaDetails({
  persona,
  aiModels,
  onClose,
  onDuplicate,
}: PersonaDetailsProps) {
  if (persona.hidden_details) return null;

  return (
    <div className="space-y-6">
      {/* Background and identity */}
      <div>
        <h3 className="mb-4 text-lg font-semibold">Background and identity</h3>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <InputFieldLabel
              label={personaBuilderData.name.label}
              tooltip={personaBuilderData.name.tooltip}
            />
            <div className="factor-label-content rounded border border-[#00000020] bg-prompt-safecomwhite p-2">
              {persona.name}
            </div>
          </div>
          <div className="space-y-2">
            <InputFieldLabel
              label={personaBuilderData.shortTag.label}
              tooltip={personaBuilderData.shortTag.tooltip}
            />
            <div className="factor-label-content min-h-[42px] rounded border border-[#00000020] bg-prompt-safecomwhite p-2">
              {persona.short_tag || 'No short tag set'}
            </div>
          </div>
          <div className="space-y-2">
            <InputFieldLabel
              label={personaBuilderData.ageBracket.label}
              tooltip={personaBuilderData.ageBracket.tooltip}
            />
            <div className="factor-label-content rounded border border-[#00000020] bg-prompt-safecomwhite p-2">
              {persona.age_bracket}
            </div>
          </div>
          <div className="space-y-2">
            <InputFieldLabel
              label={personaBuilderData.socioEconomicStatus.label}
              tooltip={personaBuilderData.socioEconomicStatus.tooltip}
            />
            <div className="factor-label-content rounded border border-[#00000020] bg-prompt-safecomwhite p-2">
              {persona.socio_economic_status}
            </div>
          </div>
          <div className="space-y-2">
            <InputFieldLabel
              label={personaBuilderData.profession.label}
              tooltip={personaBuilderData.profession.tooltip}
            />
            <div className="factor-label-content rounded border border-[#00000020] bg-prompt-safecomwhite p-2">
              {persona.profession}
            </div>
          </div>
          <div className="space-y-2">
            <InputFieldLabel
              label={personaBuilderData.englishDialect.label}
              tooltip={personaBuilderData.englishDialect.tooltip}
            />
            <div className="factor-label-content min-h-[42px] rounded border border-[#00000020] bg-prompt-safecomwhite p-2">
              {persona.english_dialect || 'No English dialect selected'}
            </div>
          </div>
          <div className="space-y-2">
            <InputFieldLabel
              label={personaBuilderData.gender.label}
              tooltip={personaBuilderData.gender.tooltip}
            />
            <div className="factor-label-content min-h-[42px] rounded border border-[#00000020] bg-prompt-safecomwhite p-2">
              {persona.gender || 'No gender selected'}
            </div>
          </div>
          <div className="space-y-2">
            <InputFieldLabel
              label={personaBuilderData.model.label}
              tooltip={personaBuilderData.model.tooltip}
            />
            <div className="factor-label-content rounded border border-[#00000020] bg-prompt-safecomwhite p-2">
              {aiModels.find((m) => m.id === persona?.model_id)?.name ||
                'Not selected'}
            </div>
          </div>
        </div>
      </div>

      {/* Beliefs and behavioural orientation */}
      <div>
        <h3 className="mb-4 text-lg font-semibold">
          Beliefs and behavioural orientation
        </h3>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <InputFieldLabel
                label={personaBuilderData.habitChangeKnowledge.label}
                tooltip={personaBuilderData.habitChangeKnowledge.tooltip}
              />
              <div className="factor-label-content rounded border border-[#00000020] bg-prompt-safecomwhite p-2">
                {persona.habit_change_knowledge}
              </div>
            </div>
            <div className="space-y-2">
              <InputFieldLabel
                label={personaBuilderData.selfEfficacy.label}
                tooltip={personaBuilderData.selfEfficacy.tooltip}
              />
              <div className="factor-label-content rounded border border-[#00000020] bg-prompt-safecomwhite p-2">
                {persona.self_efficacy}
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <InputFieldLabel
                label={personaBuilderData.locusOfControl.label}
                tooltip={personaBuilderData.locusOfControl.tooltip}
              />
              <div className="factor-label-content rounded border border-[#00000020] bg-prompt-safecomwhite p-2">
                {persona.locus_of_control}
              </div>
            </div>
            <div className="space-y-2">
              <InputFieldLabel
                label={personaBuilderData.mindsetFlexibility.label}
                tooltip={personaBuilderData.mindsetFlexibility.tooltip}
              />
              <div className="factor-label-content rounded border border-[#00000020] bg-prompt-safecomwhite p-2">
                {persona.mindset_flexibility}
              </div>
            </div>
          </div>
          <div className="space-y-2">
            <InputFieldLabel
              label={personaBuilderData.reasonsForChattingWithBot.label}
              tooltip={personaBuilderData.reasonsForChattingWithBot.tooltip}
            />
            <div className="factor-label-content whitespace-pre-wrap rounded border border-[#00000020] bg-prompt-safecomwhite p-2">
              {persona.reasons_for_chatting}
            </div>
          </div>
          <div className="space-y-2">
            <InputFieldLabel
              label={personaBuilderData.interactionDirections.label}
              tooltip={personaBuilderData.interactionDirections.tooltip}
            />
            <div className="factor-label-content whitespace-pre-wrap rounded border border-[#00000020] bg-prompt-safecomwhite p-2">
              {persona.interaction_directions}
            </div>
          </div>
        </div>
      </div>

      {/* Health-related behaviours and history */}
      <div>
        <h3 className="mb-4 text-lg font-semibold">
          Health-related behaviours and history
        </h3>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <InputFieldLabel
                label={personaBuilderData.weight.label}
                tooltip={personaBuilderData.weight.tooltip}
              />
              <div className="factor-label-content rounded border border-[#00000020] bg-prompt-safecomwhite p-2">
                {persona.weight}
              </div>
            </div>
            <div className="space-y-2">
              <InputFieldLabel
                label={personaBuilderData.qualityOfSleep.label}
                tooltip={personaBuilderData.qualityOfSleep.tooltip}
              />
              <div className="factor-label-content rounded border border-[#00000020] bg-prompt-safecomwhite p-2">
                {persona.quality_of_sleep}
              </div>
            </div>
          </div>
          <div className="space-y-2">
            <InputFieldLabel
              label={personaBuilderData.pastWeightlossInterventions.label}
              tooltip={personaBuilderData.pastWeightlossInterventions.tooltip}
            />
            <div className="factor-label-content whitespace-pre-wrap rounded border border-[#00000020] bg-prompt-safecomwhite p-2">
              {persona.past_weightloss_interventions}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <InputFieldLabel
                label={personaBuilderData.mentalWellbeing.label}
                tooltip={personaBuilderData.mentalWellbeing.tooltip}
              />
              <div className="factor-label-content whitespace-pre-wrap rounded border border-[#00000020] bg-prompt-safecomwhite p-2">
                {persona.mental_wellbeing}
              </div>
            </div>
            <div className="space-y-2">
              <InputFieldLabel
                label={personaBuilderData.neurodiversity.label}
                tooltip={personaBuilderData.neurodiversity.tooltip}
              />
              <div className="factor-label-content whitespace-pre-wrap rounded border border-[#00000020] bg-prompt-safecomwhite p-2">
                {persona.neurodiversity || 'No neurodiversity set'}
              </div>
            </div>
            <div className="space-y-2 md:col-span-2">
              <InputFieldLabel
                label={personaBuilderData.physicalHealthCondition.label}
                tooltip={personaBuilderData.physicalHealthCondition.tooltip}
              />
              <div className="factor-label-content whitespace-pre-wrap rounded border border-[#00000020] bg-prompt-safecomwhite p-2">
                {persona.physical_health_condition}
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <InputFieldLabel
                label={personaBuilderData.levelOfEngagement.label}
                tooltip={personaBuilderData.levelOfEngagement.tooltip}
              />
              <div className="factor-label-content rounded border border-[#00000020] bg-prompt-safecomwhite p-2">
                {persona.level_of_engagement}
              </div>
            </div>
            <div className="space-y-2">
              <InputFieldLabel
                label={personaBuilderData.eatingControl.label}
                tooltip={personaBuilderData.eatingControl.tooltip}
              />
              <div className="factor-label-content rounded border border-[#00000020] bg-prompt-safecomwhite p-2">
                {persona.eating_control}
              </div>
            </div>
          </div>
          <div className="space-y-2">
            <InputFieldLabel
              label={personaBuilderData.emotionalRegulation.label}
              tooltip={personaBuilderData.emotionalRegulation.tooltip}
            />
            <div className="factor-label-content whitespace-pre-wrap rounded border border-[#00000020] bg-prompt-safecomwhite p-2">
              {persona.emotional_regulation}
            </div>
          </div>
          <div className="space-y-2">
            <InputFieldLabel
              label={personaBuilderData.timeResourcesEnvironment.label}
              tooltip={personaBuilderData.timeResourcesEnvironment.tooltip}
            />
            <div className="factor-label-content whitespace-pre-wrap rounded border border-[#00000020] bg-prompt-safecomwhite p-2">
              {persona.supportive_environment}
            </div>
          </div>
          <div className="space-y-2">
            <InputFieldLabel
              label={personaBuilderData.customField.label}
              tooltip={personaBuilderData.customField.tooltip}
            />
            <div className="factor-label-content whitespace-pre-wrap rounded border border-[#00000020] bg-prompt-safecomwhite p-2">
              {persona.custom_field || 'No custom field set'}
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-4">
        <Button
          onClick={onClose}
          variant="outline"
          className="rounded-[50px] border-prompt-safecomoutrageous-orange px-8 text-prompt-safecomoutrageous-orange hover:bg-prompt-safecomfantasy hover:text-prompt-safecomoutrageous-orange"
        >
          Close
        </Button>
        {onDuplicate && (
          <Button
            onClick={() => onDuplicate(persona)}
            className="hover:bg-prompt-safecomoutrageous-orange/90 rounded-[50px] bg-prompt-safecomoutrageous-orange px-8 text-prompt-safecomwhite"
          >
            Duplicate
          </Button>
        )}
      </div>
    </div>
  );
}
